<template>
  <div class="tw-flex tw-flex-col tw-gap-5">
    <!-- Portfolio Selection and Upload Area -->
    <div
      id="portfolio-selection"
      class="tw-flex tw-flex-col tw-gap-1"
    >
      <!-- Select or Upload -->
      <div>
        <AdvisorCoreStart @analytics-done="() => $emit('analytics-done')" />
        <!-- <label class="heading">Portfolios</label> -->
        <div class="tw-w-full tw-flex tw-gap-3 tw-items-center" />
      </div>

      <!-- Upload status -->
      <div
        v-if="isUploading"
        class="tw-flex tw-flex-col tw-gap-2 tw-text-slate-500"
      >
        <div>
          <p>Processing new statements:</p>
        </div>

        <div
          v-for="statementName in Object.keys(taskIDs)"
          :key="statementName"
          class="tw-flex tw-gap-2"
        >
          <div><i :class="statusData[taskIDs[statementName]]?.icon || 'fa fa-spinner fa-spin'" /></div>
          <div class="tw-flex tw-flex-col">
            <p>
              <span class="tw-font-bold tw-whitespace-nowrap">{{ statementName }}</span>
            </p>
            <p class="tw-text-sm">
              {{ statusData[taskIDs[statementName]]?.msg || 'Starting' }}
            </p>
          </div>
        </div>
      </div>

      <!-- Notifications -->
      <div class="tw-max-w-md">
        <!-- Notification for missing tickers suggests running magic ticker fill-->
        <b-notification
          v-if="missingTickerSymbols?.length && !isUploading"
          type="is-warning"
        >
          <div class="tw-flex tw-items-center tw-gap-3">
            <i class="fa fa-exclamation-triangle tw-text-2xl" />
            <div>
              <p class="tw-max-w-sm">
                For the following uploads or portfolios, at least some of your holdings did not have ticker symbols, so analytics will not be available for those holdings. You can edit the tickers manually in the Holdings tab
                and you can also run magic-ticker-fill from the holdings tab to automatically search for the best match tickers.
              </p>
              <p
                v-for="statement in missingTickerSymbols"
                :key="statement.statement_name"
                class="tw-font-bold"
              >
                {{ statement.statement_name }}
              </p>
            </div>
          </div>
        </b-notification>

        <b-notification
          v-if="unavailableAccountTotals?.length && !isUploading"
          type="is-warning"
        >
          <div class="tw-flex tw-items-center tw-gap-3">
            <i class="fa fa-exclamation-triangle tw-text-2xl" />
            <div>
              <p class="tw-max-w-sm">
                We were unable to verify the total value of the following statements.
                Please double check the portfolio total in the holdings tab.
              </p>
              <p
                v-for="statement in unavailableAccountTotals"
                :key="statement.statement_name"
                class="tw-font-bold"
              >
                {{ statement.statement_name }}
              </p>
            </div>
          </div>
        </b-notification>

        <b-notification
          v-if="missingAccounts?.length && !isUploading"
          type="is-warning"
        >
          <div class="tw-flex tw-items-center tw-gap-3">
            <i class="fa fa-exclamation-triangle tw-text-2xl" />
            <div>
              <p class="tw-max-w-sm">
                For the statements below, we either didn't find a summary page listing accounts + totals, or we found accounts on the summary page but no holdings for that account.
                Some statements list additional accounts in the summary page, or this may mean that some pages are missing in the upload. To be safe, please double check that all relevant accounts are included.
              </p>
              <p
                v-for="statement in missingAccounts"
                :key="statement.statement_name"
                class="tw-font-bold"
              >
                {{ statement.statement_name }}
              </p>
            </div>
          </div>
        </b-notification>

        <!-- Warning when holdings sum doesn't match up with account total -->
        <!-- Added !isUploading condition so notification re-opens if needed after being closed -->
        <b-notification
          v-if="(suspectAccounts?.length && !results?.account_totals_unavailable && !results?.missing_accounts && !isUploading)"
          type="is-warning"
        >
          <div class="tw-flex tw-items-center tw-gap-5">
            <i class="fa fa-exclamation-triangle tw-text-2xl" />
            <div class="tw-max-w-sm">
              <p class="tw-mb-3">
                For the statements below, the sum of holdings does not match the total in the summary section. Please double check that the document uploaded includes all pages that contain holdings for the account. The YourStake Team has been notified and will correct and complete the upload and notify you within one business day.
              </p>
              <p
                v-for="statement in suspectAccounts"
                :key="statement.name"
                class="tw-font-bold"
              >
                {{ statement.name }}
              </p>
            </div>
          </div>
        </b-notification>

        <!-- Error message when document not processed (background task not created) -->
        <b-notification
          v-if="errorUploads?.length"
          type="is-link"
        >
          <div class="tw-flex tw-flex-col tw-items-center tw-gap-3">
            <p class="tw-max-w-sm">
              Something went wrong while trying to upload the documents below. Please make sure the files below are PDFs and the size of each PDF is below 50.0 MB.
            </p>
            <p
              v-for="statement in errorUploads"
              :key="statement"
              class="tw-font-bold tw-max-w-sm"
            >
              {{ statement }}
            </p>
          </div>
        </b-notification>

        <!-- Error message when one or more statement uploads fail -->
        <b-notification
          v-if="failedUploads?.length && !isUploading"
          type="is-link"
        >
          <div class="tw-flex tw-flex-col tw-items-center tw-gap-3">
            <p class="tw-max-w-sm">
              Something went wrong while trying to upload and process the documents below. Our system identified the document(s) you uploaded as: {{ documentTypes }}.
              The YourStake Team has been notified and will correct and complete the upload if it was a brokerage statement and notify you within one business day.
              You can still download the raw data extracted from the pdf(s) below.
            </p>
            <p
              v-for="statement in failedUploads"
              :key="statement"
              class="tw-font-bold tw-max-w-sm"
            >
              {{ statement }}
            </p>
          </div>
          <div
            v-if="filteredZipFiles.length"
            class="tw-flex tw-flex-col tw-gap-2"
          >
            <a
              v-for="file in filteredZipFiles"
              :key="file.filename + file.url"
              :href="file.url"
              class="tw-text-sm"
            >
              Zipfile of raw data for {{ file.filename }}
            </a>
          </div>
        </b-notification>

        <!-- Error message when the analytics fail -->
        <b-notification
          v-if="analyticsFailed"
          type="is-link"
        >
          <div class="tw-flex tw-items-center tw-gap-3">
            <p class="tw-max-w-sm">
              Something went wrong while trying to analyze this statement. The YourStake Team has been notified and will correct and complete the upload and notify you within one business day.
            </p>
          </div>
        </b-notification>

        <b-notification
          v-if="checkupStore.optimizationAdditionalWarnings?.warnings?.length > 0 && !isUploading"
          type="is-warning"
        >
          <div class="tw-flex tw-items-center tw-gap-5">
            <i class="fa fa-exclamation-triangle tw-text-2xl" />
            <div class="tw-w-full">
              <div
                v-for="(warning, index) in checkupStore.optimizationAdditionalWarnings?.warnings"
                v-if="checkupStore.optimizationAdditionalWarnings?.warnings"
                :key="index"
              >
                <p class="tw-font-bold class tw-mb-3">
                  - {{ warning }}
                </p>
              </div>
              <div v-if="checkupStore.optimizationAdditionalWarnings?.suggestion">
                <p class="tw-font-bold tw-mb-3">
                  {{ checkupStore.optimizationAdditionalWarnings?.suggestion }}
                </p>
                <p
                  v-if="checkupStore.optimizationAdditionalWarnings?.metricNames?.length > 0"
                  class="tw-mb-3"
                >
                  Metrics:
                  <span
                    v-for="(metric, index) in checkupStore.optimizationAdditionalWarnings?.metricNames"
                    :key="index"
                  >
                    {{ metric }}<span v-if="index < checkupStore.optimizationAdditionalWarnings?.metricNames.length - 1">, </span>
                  </span>
                </p>
              </div>
              <button
                v-if="checkupStore.personEntity?.linked_entity_external_id"
                class="button"
                @click="handleValueWarningClick"
              >
                Go to workflow in order to change values
              </button>
            </div>
          </div>
        </b-notification>
      </div>
    </div>


    <!-- Benchmark Selection Area -->
    <div>
      <div class="tw-w-full tw-flex tw-gap-3 tw-items-center" />
    </div>
    <o-modal
      :active.sync="isBenchmarkCreationModalActive"
      content-class="tw-h-1/2 tw-w-1/2"
    >
      <PortfolioCreator @submit="setBenchmarkFromCreator" />
    </o-modal>


    <!-- Submit Button and Loading Status -->
    <div class="tw-flex tw-flex-col tw-gap-4">
      <div
        v-if="csvFiles.length"
        class="tw-flex tw-flex-col tw-gap-1"
      >
        <p class="tw-font-semibold tw-text-lg">
          Spreadsheets to download
        </p>
        <a
          v-for="file in csvFiles"
          v-if="file"
          :key="file?.filename"
          :href="file?.url"
          class="tw-text-sm"
        >
          Spreadsheet for {{ file?.filename }}
        </a>
      </div>
    </div>
  </div>
</template>


<script>
// Components
import TypeaheadPortfolioSearch from '@frontend/dash3/js/components/portfolios/TypeaheadPortfolioSearch.vue';
import PortfolioCreator from '@frontend/ui/components/utils/PortfolioCreator.vue';


// Stores
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { useChatStore } from '@frontend/ui/stores/chatStore';

// Helpers and other imports
import { newWorkbook, getWorkbookURL, createCSVZip, getZipFileURL } from '@frontend/shared/exceljs/helpers';
import { awaitTask } from '@frontend/ui/features/checkup/helpers/tasks';
import { postSendInternalEmail } from '@frontend/api/emailsApi.js';
import * as Sentry from '@sentry/vue';
import { RISK_LEVELS } from '@frontend/ui/features/checkup/helpers/checkupFunctions.js';
import PercentageInput from '@frontend/ui/components/utils/PercentageInput.vue';
import AdvisorCoreStart from '@frontend/ui/features/checkup/components/AdvisorCoreStart.vue';

export default {
  name: 'PortfolioUploadSection',
  components: { TypeaheadPortfolioSearch, PortfolioCreator, PercentageInput, AdvisorCoreStart },
  props: [],
  data() {
    return {
      files: [],
      url: null,
      csvFile: {},
      isUploading: false,
      bestfitloading: false,

      selectedBenchmark: null,
      selectedPortfolio: null,

      isBenchmarkLoading: false,
      isPortfolioLoading: false,
      taskFailed: false,
      failedTasks: [], // This replaces taskFailed which was before multiple uploads
      uploadErrors: [], // store files that had upload errors
      taskCheckInterval: null,

      statusMessage: 'Starting',
      statusData: {},

      taskIDs: {},
      portfolioID: null,

      uploadResults: {},
      previousUploadResults: {},
      csvFiles: [],
      zipFiles: [],
      csvFiles_MTF: [],
      // Results of the analysis will go here
      results: {
        csv: null,
        holdings_summary: null,
        summary_account_totals: null,
      },
      analyticsFailed: false,

      isBenchmarkCreationModalActive: false,
    };
  },
  computed: {
    ...mapStores(useCheckupStore, useAdvisorStore, useChatStore),
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
      };
    },
    documentTypes() {
      const types = this.zipFiles.map(file => file.document_type);
      if (types.length <= 1) {
        return types.join('');
      }
      return types.slice(0, -1).join(', ') + ', and ' + types[types.length - 1];
    },
    filteredZipFiles() {
      return this.zipFiles.filter(file => file !== null && file !== undefined);
    },
    mockStatement() {
      return null; // lol
      if (!this.file) return null;
      let name = this.file.name;

      if (!name.startsWith('mock_')) return null;
      name = name.replace('mock_', '');
      return name.includes('.') ? name.split('.')[0] : name;
    },
    activeStatus() {
      const statuses = Object.values(this.statusData);
      if (!statuses.length) return { };

      const sorted = statuses.sort((a, b) => a.progress - b.progress);
      return sorted[0];
    },
    failedUploads() {
      return this.failedTasks?.map(task => task.meta.statement_name);
    },
    errorUploads() {
      return this.uploadErrors?.map(task => task.meta.statement_name);
    },
    suspectAccounts() {
      const completeSuspectAccounts = {};

      Object.entries(this.previousUploadResults).forEach(([portfolioId, uploadResult]) => {
        const holdingsByAccount = this.getHoldingsByAccount(uploadResult.csv);

        const summaryAccountTotals = uploadResult.summary_account_totals;
        if (!summaryAccountTotals) return;

        const accuracyCheckResults = this.accuracyCheckResults(summaryAccountTotals, holdingsByAccount);

        const suspectAccounts = accuracyCheckResults.filter(result => {
          return !result.pass;
        }).map(result => result.account);

        if (suspectAccounts.length) {
          completeSuspectAccounts[portfolioId] = { name: uploadResult.statement_name, suspectAccounts };
        }
      });
      return Object.values(completeSuspectAccounts);
    },
    unavailableAccountTotals() {
      const results = this.previousUploadResults;
      if (!results) return [];
      return Object.values(results).filter(statement => statement.account_totals_unavailable);
    },
    missingTickerSymbols() {
      const results = this.previousUploadResults;
      if (!results) return [];
      return Object.values(results).filter(statement => statement.missing_tickers);
    },
    missingAccounts() {
      const results = this.previousUploadResults;
      if (!results) return [];
      return Object.values(results).filter(statement => statement.missing_accounts);
    },

    totalsByAccount() {
      const byAccount = { };

      this.checkupStore.portfolio?.holdings.forEach(holding => {
        const account = holding.account;
        const dollars = holding.dollars;

        const current = byAccount[account] || 0;
        byAccount[account] = current ? current + dollars : dollars;
      });

      // Get total of all accounts and add to object
      const total = Object.values(byAccount).reduce((a, b) => a + b, 0);
      return { total, accountTotals: Object.keys(byAccount).map(key => ({ name: key, total: byAccount[key] })) };
    },
    readyToSubmit() {
      return !!this.checkupStore.portfolios?.length && !!this.checkupStore.benchmark;
    },
  },
  watch: {
    'checkupStore.optimization': {
      handler(newVal, oldVal) {
        this.checkupStore.optimizationAdditionalWarnings = this.computeWarnings();
      },
      deep: true,
    },
    'checkupStore.areas': {
      handler(newVal, oldVal) {
        this.checkupStore.optimizationAdditionalWarnings = this.computeWarnings();
      },
      deep: true,
    },
    'activeStatus': function() {
      this.statusMessage = this.activeStatus?.msg;
    },
    'results.csv': async function(newCSV) {
      console.log('Watcher for results.csv triggered');
      console.log('newCSV', newCSV);
      console.log('this.checkupStore.portfolio', this.checkupStore.portfolio);
      const name = this.checkupStore.portfolio?.name;
      console.log(name);
      const csvData = await this.createSpreadsheet(name, newCSV);
      this.csvFiles = [csvData];
      console.log('RESULTS.CSV', csvData);
    },
    'previousUploadResults': function(uploadResults) {
      const newFiles = [];
      Object.entries(uploadResults).forEach(async ([portfolioId, results]) => {
        console.log('RESULTS', results);
        const createdCsvData = await this.createSpreadsheet(results.statement_name, results.csv);
        if (createdCsvData) newFiles.push(createdCsvData);
      });
      this.csvFiles = newFiles;
      console.log('UPLOAD RESULTS', newFiles);
    },

    'checkupStore.benchmark': function(newBenchmark) {
      if (this.selectedBenchmark?.id != newBenchmark?.id) {
        this.selectedBenchmark = newBenchmark;
      }
    },
    // Runs whenever selected portfolios change
    // Fetch holdings and keep in checkupStore.holdings
    'checkupStore.portfolios': async function(newPortfolios) {
      const newHoldings = {};

      // Doing it this way ensures that we also remove holdings when a portfolio is unselected
      for (const portfolio of newPortfolios.filter(p => p.label === 'portfolio')) {
        // If we already have the holdings just copy them over
        if (this.checkupStore.holdingsByPortfolio[portfolio.external_id]) {
          newHoldings[portfolio.external_id] = this.checkupStore.holdingsByPortfolio[portfolio.external_id];
        }
        // Otherwise fetch them from the backend
        else {
          const holdings = await this.getPortfolioHoldings(portfolio.external_id);
          newHoldings[portfolio.external_id] = holdings;
        }
      }
      // Update the holdings in the checkupStore
      this.checkupStore.holdingsByPortfolio = newHoldings;
      console.log('checkupStore.holdingsByPortfolio', this.checkupStore.holdingsByPortfolio);
    },
  },
  updated() {

  },
  async created() {
    this.getDefaultBenchmark();
    // check for params, if they're there get the check up and call selectBenchmark and selectExistingPortfolio
    const checkupId = new URLSearchParams(window.location.search).get('latest_checkup');
    const portfolioId = new URLSearchParams(window.location.search).get('portfolio');
    const benchmarkId = new URLSearchParams(window.location.search).get('benchmark');
    const proposedId = new URLSearchParams(window.location.search).get('proposed');
    const personEntityID = new URLSearchParams(window.location.search).get('person_entity_id');
    const inclusions = new URLSearchParams(window.location.search).get('inclusions');
    const exclusions = new URLSearchParams(window.location.search).get('exclusions');
    const useRiskBenchmark = new URLSearchParams(window.location.search).get('use_risk_benchmark');
    const riskNumberFromURL = new URLSearchParams(window.location.search).get('client_risk'); // used when clicking from past analyses where risk number can be any numerical value
    const manualRisk = new URLSearchParams(window.location.search).get('manual_risk'); // used when clicking from vadis where risk level is a string
    const valuesFromURL = new URLSearchParams(window.location.search).get('values');
    const fundScreenerFund = new URLSearchParams(window.location.search).get('fund_screener_fund');
    const optimizeParam = new URLSearchParams(window.location.search).get('optimize'); // explicitly enable optimization
    const customBenchmarkId = new URLSearchParams(window.location.search).get('custom_benchmark_id');
    const customBenchmarkTicker = new URLSearchParams(window.location.search).get('custom_benchmark_ticker_symbol');
    const customBenchmarkName = new URLSearchParams(window.location.search).get('custom_benchmark_name');

    console.log('checkupId query param: ', checkupId);
    console.log('portfolioId query param: ', portfolioId);
    console.log('benchmarkId query param: ', benchmarkId);
    console.log('proposedId query param: ', proposedId);
    console.log('personEntityID query param: ', personEntityID);

    if (valuesFromURL) {
      const values_url = '/api/v1/metric_descriptions';
      const params = {};
      params['screen_sets'] = valuesFromURL;
      const queryString = (new URLSearchParams(params)).toString();
      const url = `${values_url}?${queryString}`;
      const response = await fetch(url);
      const data = await response.json();
      this.checkupStore.setSelectedValues(data);
    }
    // Set optimizer flag based on URL parameter (defaults to false)
    if (optimizeParam === 'true') {
      this.checkupStore.optimizerChecked = true;
    }

    if (customBenchmarkId || customBenchmarkTicker) {
      this.checkupStore.customBenchmark = {
        entityId: customBenchmarkId || null,
        tickerSymbol: customBenchmarkTicker || null,
        name: customBenchmarkName || customBenchmarkTicker || customBenchmarkId,
      };
      let customBenchmarkEntity = null;
      if (customBenchmarkId) {
        customBenchmarkEntity = await this.checkupStore.getEntityLight(customBenchmarkId);
      } else if (customBenchmarkTicker) {
        const [portfolioEntity] = await this.checkupStore.getEntityLightFromFund(customBenchmarkTicker);
        customBenchmarkEntity = portfolioEntity;
      }
      if (customBenchmarkEntity) {
        this.checkupStore.setBenchmark(customBenchmarkEntity);
      }
    }

    if (manualRisk) {
      const riskLevel = RISK_LEVELS.find(level => level.label === manualRisk);
      if (riskLevel) {
        this.checkupStore.setManualClientRisk(riskLevel);
      } else {
        const err = new Error('Invalid manual risk level:', manualRisk);
        console.error(err);
        Sentry.captureException(err);
      }
    } else if (riskNumberFromURL) {
      this.checkupStore.setManualClientRisk({ value: parseInt(riskNumberFromURL) });
    }
    if (fundScreenerFund) {
      const [portfolio, benchmark] = await this.checkupStore.getEntityLightFromFund(fundScreenerFund);
      // Add with 'portfolio' label for it to show up in the portfolio search box
      this.checkupStore.addPortfolio({
        ...portfolio,
      }, 'portfolio');
      this.checkupStore.setCombinedPortfolio(portfolio);
      this.checkupStore.setBenchmark(benchmark);
      this.$emit('run-analysis-now');
    } else {
      const accountValue = parseFloat(new URLSearchParams(window.location.search).get('account_value'));
      if (!isNaN(accountValue)) {
        this.checkupStore.setAccountValue(accountValue);
      }
      const accountsParam = new URLSearchParams(window.location.search).get('accounts');
      if (accountsParam) {
        const decodedAccountsJson = decodeURIComponent(accountsParam);
        const accounts = JSON.parse(decodedAccountsJson);
        this.checkupStore.setAccounts(accounts);
      }

      if (inclusions) {
        const inclusionsArray = inclusions.split(',');
        inclusionsArray.forEach(inclusion => {
          this.checkupStore.specificInclusions.push(inclusion.trim());
        });
      }

      if (exclusions) {
        const exclusionsArray = exclusions.split(',');
        exclusionsArray.forEach(exclusion => {
          this.checkupStore.specificExclusions.push(exclusion.trim());
        });
      }
      if (personEntityID) {
        console.error('THIS IS Person Entity ID HERE', personEntityID);
        await this.getPortfolioCheckupPersonEntityInfo(personEntityID, useRiskBenchmark);
      }
      if (portfolioId) {
        const portfolio = (await this.checkupStore.getEntityLight(portfolioId));

        // Only add portfolio if it has actual holdings
        if (portfolio?.shareownership_or_uploaded_df_agg_exists) {
          this.checkupStore.addPortfolio({
            ...portfolio,
          }, 'portfolio');
        } else {
          console.log('Portfolio has no holdings, skipping:', portfolio?.name || portfolio?.external_id);
        }
      }
      if (proposedId) {
        const proposedPortfolio = (await this.checkupStore.getEntityLight(proposedId));
        this.checkupStore.addPortfolio({
          ...proposedPortfolio,
        }, 'optimizedPortfolio');
      }
      if (benchmarkId) {
        const benchmark = (await this.checkupStore.getEntityLight(benchmarkId));
        this.checkupStore.setBenchmark(benchmark);
      }
      if (checkupId) {
        console.log('INSIDE IF CHECKUP ID');
        try {
          this.checkupStore.setLoadingState('isPreferencesLoading', true);
          const { portfolio_id, benchmark_id, proposed_id, client_risk, values, specific_inclusions, specific_exclusions, specific_donotbuysell, current_json, benchmark_json, proposed_json, created, person_entity_id } = await this.checkupStore.getCheckup(checkupId);
          if (client_risk) {
            const riskLevel = RISK_LEVELS.find(risk => risk.value === client_risk);

            if (riskLevel) {
              this.checkupStore.setManualClientRisk({
                label: riskLevel.label,
                value: riskLevel.value,
              });
            } else {
              this.checkupStore.setManualClientRisk({ value: client_risk });
            }
            if (values) {
              const values_url = '/api/v1/metric_descriptions';
              const params = {};
              params['screen_sets'] = values;
              const queryString = (new URLSearchParams(params)).toString();
              const url = `${values_url}?${queryString}`;
              const response = await fetch(url);
              const data = await response.json();
              this.checkupStore.setSelectedValues(data);
            }
          }

          if (portfolio_id) {
            const portfolio = (await this.checkupStore.getEntityLight(portfolio_id));
            // Add with 'portfolio' label for it to show up in the portfolio search box
            this.checkupStore.addPortfolio({
              ...portfolio,
            }, 'portfolio');
            // add the 'combinedPortfolio' label for the analytics to run
            this.checkupStore.setCombinedPortfolio(portfolio);
          }
          if (benchmark_id) {
            const benchmark = (await this.checkupStore.getEntityLight(benchmark_id));
            this.checkupStore.setBenchmark(benchmark);
          }
          if (proposed_id) {
            const proposed = (await this.checkupStore.getEntityLight(proposed_id));
            this.checkupStore.setOptimizedPortfolio(proposed);
          }
          if (current_json) {
            // Need to check if nickname is a key because that's from an older portfolio checkup that ran on a modelportfolio instead of entity.
            if (current_json.nickname && !current_json.name) {
              current_json.name = current_json.nickname;
              delete current_json.nickname;
            }
            if (current_json.external_id && current_json.external_id.startsWith('modelportfolio')) {
              current_json.external_id = portfolio_id;
            }
            this.checkupStore.updatePortfolio(current_json);
          }
          if (benchmark_json) {
            if (benchmark_json.nickname && !benchmark_json.name) {
              benchmark_json.name = benchmark_json.nickname;
              delete benchmark_json.nickname;
            }
            if (benchmark_json.external_id && benchmark_json.external_id.startsWith('modelportfolio')) {
              benchmark_json.external_id = benchmark_id;
            }
            this.checkupStore.updatePortfolio(benchmark_json);
          }
          if (proposed_json) {
            if (proposed_json.nickname && !proposed_json.name) {
              proposed_json.name = proposed_json.nickname;
              delete proposed_json.nickname;
            }
            if (proposed_json.external_id && proposed_json.external_id.startsWith('modelportfolio')) {
              proposed_json.external_id = proposed_id;
            }
            this.checkupStore.updatePortfolio(proposed_json);
          }
          this.$emit('analytics-done');
          console.log('analytics emitted here');

          if (personEntity) {
            console.error('PERSON ENTITY with id in created', personEntity);
            const response = await fetch(`/api/v1/get-risk-score/${personEntity.external_id}/`);
            const data = await response.json();
            this.checkupStore.setPersonEntity({ ...personEntity, risk_score: data.risk_score });
          }
          this.checkupStore.setLoadingState('isPreferencesLoading', false);
          return;
        } catch (err) {
          console.warn('Latest checkup callout failed, defaulting to normal behavior');
        }
      }
    }
  },
  beforeDestroy() {
    clearInterval(this.taskCheckInterval);
  },
  methods: {
    async getPortfolioCheckupPersonEntityInfo(personEntityID, useRiskBenchmark) {
      this.checkupStore.setLoadingState('isPreferencesLoading', true);
      const baseUrl = `/api/v1/portfolio_checkup_person_entity_info/${personEntityID}/`;
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.append('client_risk', this.checkupStore.clientRisk?.value || 0);
      console.error('URL IS HERE', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...this.headers,
          'Content-Type': 'application/json',
        },
      });
      const { personEntity, risk_based_benchmark } = await response.json();
      if (risk_based_benchmark && this.checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization')) {
        this.checkupStore.riskBasedBenchmark = risk_based_benchmark;
        if (useRiskBenchmark) this.checkupStore.setBenchmark(risk_based_benchmark);
      }
      if (personEntity) {
        console.error('PERSON ENTITY with id in getinfo', personEntity);
        const response = await fetch(`/api/v1/get-risk-score/${personEntity.external_id}/`);
        const data = await response.json();
        this.checkupStore.setPersonEntity({ ...personEntity, risk_score: data.risk_score });
      }
      this.checkupStore.setLoadingState('isPreferencesLoading', false);
      return;
    },
    computeWarnings() {
      console.log('Checking for optimizer warnings');
      const warnings = [];
      const optimizations = this.checkupStore.optimization?.optimizations || [];

      const benchmarkName = this.checkupStore.benchmark?.name;
      if (!optimizations.length) {
        console.log('Optimizer has not been run, so need to check warnings.');
        return [];
      }

      if (!benchmarkName || !benchmarkName.includes('VADIS')) {
        console.log('Not a vadis benchmark, so dont need to compute warnings.');
        return [];
      }

      const tooHighTrackingErrors = optimizations.map(opt => opt.diagnostics?.tracking_error * 100).filter(TE => TE > 2.25);

      tooHighTrackingErrors.forEach(TE => {
        warnings.push(`Tracking Error (TE) is greater than 2.25%. Actual: ${TE.toFixed(2)}%`);
      });

      const sectorBreakdownArea = this.checkupStore.areas.find(area => area.name === 'Equity Sector Breakdown');
      if (sectorBreakdownArea) {
        const sectorExceedsBenchmark = sectorBreakdownArea.optimized.some(sector => {
          const benchmarkSector = sectorBreakdownArea.proposal.find(s => s.name === sector.name);
          return benchmarkSector && sector.value > benchmarkSector.value * 1.75;
        });
        if (sectorExceedsBenchmark) {
          warnings.push('You have at least one sector with an exposure 75% greater than the benchmark.');
        }
      }

      const marketCapBreakdownArea = this.checkupStore.areas.find(area => area.name === 'Market Cap Breakdown');
      if (marketCapBreakdownArea) {
        const marketCapDeviationExists = marketCapBreakdownArea.optimized.some((currentMarketCap, index) => {
          const proposalMarketCap = marketCapBreakdownArea.proposal[index];
          if (!proposalMarketCap) return false;
          const deviation = Math.abs(currentMarketCap.value - proposalMarketCap.value);
          return deviation > 2;
        });

        if (marketCapDeviationExists) {
          warnings.push('Your market cap deviation vs. the benchmark is greater than 2%.');
        }
      }

      const valuesArea = this.checkupStore.areas.find(area => area.name === 'Values Alignment');
      if (valuesArea) {
        valuesArea.optimized.forEach((currentValue, index) => {
          const proposalValue = valuesArea.proposal[index];
          if (proposalValue && currentValue.value < proposalValue.value) {
            warnings.push(`${currentValue.name} value is worse than the benchmark.`);
          }
        });
      }
      const metricNames = [];
      let suggestion = '';
      if (warnings.length > 0) {
        const highImpactMetrics = ['Animal Exploitation',
          'Animal Testing',
          'Anti-Forced Labor Score',
          'CEO Pay',
          'Data Privacy Violations',
          'Data Privacy Violations Intensity',
          'Digital Rights Freedom of Expression Score',
          'Digital Rights Governance Score',
          'Digital Rights Privacy Score',
          'Digital Rights Ranking',
          'Discrimination Violations Intensity',
          'Education Technology & Support Exposure',
          'Fair Labor Affiliation',
          'Financial Crimes',
          'Financial Crimes Intensity',
          'Migrant Detention Involvement',
          'Occupation Involvement',
          'Pharmaceutical Exposure (anti-pharma)',
          'Political Spending Disclosure Score',
          'Prison Industry Exposure',
          'Pro-NAACP Political Spending',
          'Russia Exposure',
          'Strong Climate Commitments',
          'Uyghur Forced Labor Involvement',
          'Violent Entertainment Exposure',
          'Water Withdrawal',
          'Women on Boards'];
        const metricsInPortfolio = this.checkupStore.completeValues?.flatMap(completeValue => completeValue?.metrics?.map(metric => metric.name) || []);
        const impactfulMetricsFound = metricsInPortfolio.filter(metricName => highImpactMetrics.includes(metricName));
        suggestion = 'Try removing some values selected';
        if (impactfulMetricsFound.length > 0) {
          suggestion = 'If you would like to remove these warnings, you can remove some values from your values set. Here are some metrics that have a high impact on portfolio composition, try removing them and re-running the optimization.';
          impactfulMetricsFound.forEach(metricName => {
            metricNames.push(metricName);
          });
        }
      }
      return { warnings, suggestion, metricNames };
    },
    handleValueWarningClick() {
      const personEntityID = this.checkupStore.personEntity?.linked_entity_external_id;
      const url = `/advisor-dashboard/direct-indexing/${personEntityID}?step=values`;
      window.open(url, '_blank');
    },
    getHoldingsByAccount(csvData) {
      const byAccount = { };

      csvData?.forEach(holding => {
        const account = holding['Account'];
        if (!account) return;
        const currency = holding['Market Value'];
        const number = this.currencyToNumber(currency);

        const current = byAccount[account];
        byAccount[account] = current ? current + number : number;
      });
      return byAccount;
    },
    accuracyCheckResults(summaryAccountTotals, holdingsByAccount) {
      // We can no longer rely on consistent account naming and account number extraction.
      // Instead, we will loop through holdings totals and check for a matching value in the summary totals.

      return Object.keys(holdingsByAccount).map(account => {
        const total = holdingsByAccount[account];

        const matchingSummaryTotals = summaryAccountTotals.filter(summary => {
          const error = Math.abs(total - summary['Total']);
          return error < 5; // Considered a match if difference is less than $5
        });

        return {
          account,
          total,
          matchingSummaryTotals,
          pass: matchingSummaryTotals.length > 0,
        };
      });
    },
    setBenchmarkFromCreator(newBenchmark) {
      this.selectBenchmark(newBenchmark);
      this.isBenchmarkCreationModalActive = false;
    },

    async getDefaultBenchmark() {
      this.selectBenchmark({ id: 'default_benchmark', name: 'S&P 500 (SPY)' });
    },

    currencyToNumber(currency) {
      if (!currency) return 0;
      if (typeof currency !== 'string') return 0;
      // Remove any character except for numbers and periods
      const clean = currency.replace(/[^\d.]/g, '');
      return Number(clean);
    },

    selectBenchmark(benchmark) {
      console.log('Selecting Benchmark');
      this.selectedBenchmark = benchmark;
      console.log('selectBenchmark', benchmark);
      this.checkupStore.setBenchmark(benchmark);
    },

    async getPortfolioHoldings(external_id) {
      const url = `/api/v1/entities/${external_id}/holdings/`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          ...this.headers,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();

      // Matched holdings
      const matched = data.portfolio.holdings;

      // Unmatched holdings
      const unmatched = data.portfolio.unmatched_holdings;

      return [...matched, ...unmatched];
    },

    async createConversation() {
      const convoID = await this.chatStore.currentConversationId;
      console.log('Current conversation ID', convoID);
      const url = '/api/v1/checkup/chat/create-conversation/';
      const body = {
        conversation_id: convoID,
        portfolio_id: this.checkupStore.portfolio.external_id,
        benchmark_id: this.checkupStore.benchmark.id,
      };
      console.log('CALLING AWAIT TASK');
      const data = await awaitTask(url, {
        method: 'POST',
        headers: { ...this.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    },

    async sendStatement(file) {
      // Sends the pdf statement to the backend and gets a background task id
      const url = '/api/v1/process/statement_upload/';

      // Prepares the file as form data so it can be sent in the request
      const formData = new FormData();

      formData.append('statement', file);

      if (this.mockStatement) formData.append('mock', this.mockStatement);

      // Send the file
      const res = await fetch(url, {
        method: 'POST',
        headers: this.headers,
        body: formData,
      });

      // Handles a failed upload
      // Among other things, this should catch the 403 errors when a statement is blocked by AWS WAF rules.
      if (!res.ok) {
        // When upload fails before task is created, we add to this list so warning pops up
        this.uploadErrors = this.uploadErrors || [];
        this.uploadErrors.push({
          meta: { statement_name: file.name },
        });

        // Send email before throwing error
        const template = res.status === 403 ? 'upload_403' : 'upload';
        postSendInternalEmail(`portfolio_checkup/error/${template}.yaml`);

        // Throw an error with the server response details
        // Should get picked up by Sentry
        const responseBody = await res.text();
        const errorContext = {
          status: res.status,
          statusText: res.statusText,
          responseBody,
        };
        throw new Error('Server error while trying to upload statement: ' + JSON.stringify(errorContext));
      }

      // Get the task id from the response
      const data = await res.json();
      if (!data.task_ids) return;

      // TODO: Could use some error handling
      const ids = Object.values(data.task_ids);
      this.taskIDs = { ...this.taskIDs, [file.name]: ids[0] };
    },

    updateStatusDetails(task) {
      if (!task.meta.status) return;
      const status = task.status || 'unknown';

      const spinner = 'fa-spinner fa-spin';

      const icons = {
        'pending': spinner,
        'failed': 'fa-exclamation-triangle',
        'complete': 'fa-check',
      };

      const details = {
        ...task.meta.status,
        status: task.status,
        icon: `fa ${icons[status] || spinner}`,
      };

      this.statusData = {
        ...this.statusData,
        [task.id]: details,
      };
    },

    // handleFailedTask(task) {
    //   // clearInterval(this.taskCheckInterval);
    //   console.error('Background task failed', task);

    //   const error = task.meta.error;
    //   console.error(error.type + ': ' + error.message);
    //   console.log(error.traceback);

    //   // this.isUploading = false;
    //   this.taskFailed = true;

    //   if (!this.failedTasks.find(failedTask => failedTask.id === task.id)) {
    //     this.failedTasks = [...this.failedTasks, task];
    //   }
    // },

    async handleFailedTask(task) {
      console.error('Background task failed', task);

      if (task.meta.error) {
        const error = task.meta.error;
        console.error(error.type + ': ' + error.message);
        console.log(error.traceback);
      }

      // Check if there is raw data from the task and if so put it in a zipfile
      if (task.meta.raw_data) {
        try {
          const documenttype = task.meta.document_type;
          const zip = createCSVZip(task.meta.raw_data);
          const zipUrl = await getZipFileURL(zip);
          const newFile = {
            filename: task.meta.statement_name + '.zip',
            url: zipUrl,
            document_type: documenttype,
          };

          if (!this.zipFiles.some(file => file.filename === newFile.filename)) {
            this.zipFiles.push(newFile);
          }
        } catch (error) {
          console.error('Error creating ZIP file:', error);
        }
      }

      this.taskFailed = true;
      if (!this.failedTasks.find(failedTask => failedTask.id === task.id)) {
        this.failedTasks = [...this.failedTasks, task];
      }
    },

    saveTaskMetaData(task) {
      const portfolioID = task.meta.portfolio_id;
      if (portfolioID) this.portfolioID = portfolioID;

      // New upload results for the portfolio handled by this task
      const newResults = { ...this.uploadResults[portfolioID] } || {};

      if (task.meta.csv) {
        // console.log('UPLOAD RESULTS HERE', this.uploadResults);
        // this.uploadResults = {
        //   // ...this.uploadResults,
        //   [task.meta.portfolio_id]: {
        //     ...this.uploadResults[task.meta.portfolio_id],
        //     csv: JSON.parse(task.meta.csv),
        //   },
        // };
        // console.log('NEW UPLOAD RESULTS', this.uploadResults);
        newResults.csv = JSON.parse(task.meta.csv);
      };

      // Copy meta data over to results object from task
      [
        'holdings_summary',
        'summary_account_totals',
        'account_totals_unavailable',
        'missing_accounts',
        'missing_tickers',
        'optimization_id',
        'statement_name',
      ].forEach(key => {
        if (task.meta[key]) this.results[key] = task.meta[key];
        if (task.meta[key]) newResults[key] = task.meta[key];
      });
      console.log('EDITING UPLOAD RESULTS', 'OLD UPLOAD RESULTS:', this.uploadResults);
      this.uploadResults = {
        ...this.uploadResults,
        [portfolioID]: newResults,
      };
      this.previousUploadResults = { // this is because uploadResults get cleared at the end of the process, but we need something to keep track of all results, for suspectAccounts
        ...this.previousUploadResults,
        [portfolioID]: newResults,
      };
      console.log('FINISHED EDITING UPLOAD RESULTS', 'NEW UPLOAD RESULTS:', this.uploadResults);
    },

    async createSpreadsheet(filename, holdings) {
      if (!holdings?.length) return null;
      console.log('Creating spreadsheet for ' + filename + ' with ' + holdings.length + ' holdings. Fxn called in PortfolioUploadSection.');
      let workbook = newWorkbook();
      // Create workbook here
      // For an example see createSampleWorkbook in frontend/shared/exceljs/helpers
      // Or checkout the docs at https://github.com/exceljs/exceljs#interface

      workbook = this.createHoldingsWorksheet(workbook, holdings);
      // workbook = this.createTransactionWorksheet(workbook);
      // workbook = this.createRealizedGainLossSpreadsheet(workbook);
      // Gets the download url for the workbook
      // This will automatically be attached to the Download Results button
      const url = await getWorkbookURL(workbook);
      this.isUploading = false; // This might be redundant

      this.url = url;
      this.csvFile = { filename, url };
      return this.csvFile;
    },

    createHoldingsWorksheet(workbook, holdings) {
      const worksheet = workbook.addWorksheet('Holdings');

      // Default columns
      const columns = [
        { header: 'Symbol/CUSIP', key: 'symbol', width: 30 },
        { header: 'Name', key: 'name', width: 50 },
        { header: 'Quantity', key: 'qty', width: 30 },
        { header: 'Cost Basis', key: 'cost', width: 30 },
        { header: 'Market Value', key: 'value', width: 30 },
        { header: 'Fees', key: 'fees', width: 30 },
        { header: 'Security Type', key: 'type', width: 30 },
        { header: 'Account', key: 'account', width: 50 },
      ];

      // Check if any holdings include 'Notes', which means MTF was run and want to includes Notes column in spreadsheet
      const includesNotes = holdings.some(asset => asset.hasOwnProperty('Notes'));
      if (includesNotes) {
        columns.unshift({ header: 'Notes', key: 'notes', width: 50 }); // Insert 'Notes' at the start of the columns array
      }

      // Apply columns to worksheet
      worksheet.columns = columns;

      // Populate rows
      holdings.forEach(asset => {
        let fees = asset['Fees'];
        const isPercent = typeof fees === 'string' && fees.includes('%');

        if (!isNaN(parseFloat(fees)) && !isPercent) {
          fees = (parseFloat(fees) * 100).toFixed(2) + '%';
        }

        const row = {
          notes: asset['Notes'] || '', // Add notes to row only if it exists
          symbol: asset['Symbol/CUSIP'],
          name: asset['Name'],
          cost: asset['Cost Basis'],
          qty: asset['Quantity'],
          value: asset['Market Value'],
          account: asset['Account'],
          fees: fees,
          type: asset['Security Type'],
        };

        // delete the notes column if there aren't any notes
        if (!includesNotes) delete row.notes;

        worksheet.addRow(row);
      });

      return workbook;
    },

    createTransactionWorksheet(workbook) {
      const transactions = this.results.transactions;
      console.log('TRANSACTIONS');
      console.log(transactions);
      if (transactions.length > 0) {
        const worksheet = workbook.addWorksheet('Transactions');
        worksheet.columns = [
          { header: 'Date', key: 'date', width: 30 },
          { header: 'Type', key: 'type', width: 30 },
          { header: 'Description', key: 'desc', width: 30 },
          { header: 'Symbol/CUSIP', key: 'symbol', width: 30 },
          { header: 'Quantity', key: 'qty', width: 30 },
          { header: 'Price', key: 'price', width: 30 },
          { header: 'Amount', key: 'amount', width: 30 },
          { header: 'Account', key: 'account', width: 30 },
        ];

        transactions.forEach(t => {
          worksheet.addRow({
            date: t['Date'],
            type: t['Transaction Type'],
            desc: t['Description'],
            symbol: t['Symbol/CUSIP'],
            qty: t['Quantity'],
            price: t['Price'],
            amount: t['Transaction Amount'],
            account: t['Account Number'],
          });
        });
        return workbook;
      } else {
        return workbook;
      }
    },

    createRealizedGainLossSpreadsheet(workbook) {
      const realized_gain_loss = this.results.realized_gains_and_losses;
      console.log('Realized Gain Loss');
      console.log(realized_gain_loss);
      if (typeof realized_gain_loss.length === 'undefined' || realized_gain_loss.length === 0) {
        return workbook;
      } else {
        const worksheet = workbook.addWorksheet('Realized Gains and Losses');

        worksheet.columns = [
          { header: 'Security', key: 'security', width: 30 },
          { header: 'Symbol/CUSIP', key: 'symbol', width: 30 },
          { header: 'Quantity', key: 'qty', width: 30 },
          { header: 'Acquired Date', key: 'acquired', width: 30 },
          { header: 'Sold Date', key: 'sold', width: 30 },
          { header: 'Short/Long Term', key: 'term', width: 30 },
          { header: 'Cost Basis', key: 'cost', width: 30 },
          { header: 'Total Proceeds', key: 'proceeds', width: 30 },
          { header: 'Realized Gain/Loss', key: 'perf', width: 30 },
          { header: 'Account Number', key: 'account', width: 30 },
        ];

        realized_gain_loss.forEach(item => {
          worksheet.addRow({
            security: item['Security'],
            symbol: item['Symbol/CUSIP'],
            qty: item['Quantity'],
            acquired: item['Acquired Date'],
            sold: item['Sold Date'],
            term: item['Short/Long Term'],
            cost: item['Cost Basis'],
            proceeds: item['Total Proceeds'],
            perf: item['Realized Gain/Loss'],
            account: item['Account Number'],
          });
        });
        return workbook;
      }
    },

  },
};
</script>


<style scoped>

</style>
